import type { Components } from '@webapp/api/integrations/client'
import type { IntegrationSetupState } from '@webapp/components/IntegrationPlatformCard/types'
import { IntegrationSetupStates } from '@webapp/components/IntegrationPlatformCard/types'
import {
  type IntegrationPlatform,
  type IntegrationServicePlatform,
  type IntegrationCodatConfig,
  IntegrationServicePlatforms,
} from '@webapp/models/enums'
import { IntegrationCodatConfigStatuses } from '@webapp/models/enums/IntegrationCodatConfigStatus'
import { IntegrationStatuses } from '@webapp/models/enums/IntegrationStatus'

export const isCodatServicePlatform = (
  platform: IntegrationPlatform
): platform is IntegrationServicePlatform =>
  platform === IntegrationServicePlatforms.QUICKBOOKS_ONLINE ||
  platform === IntegrationServicePlatforms.QUICKBOOKS_ONLINE_SANDBOX ||
  platform === IntegrationServicePlatforms.SAGE_INTACCT ||
  platform === IntegrationServicePlatforms.CODAT_NETSUITE ||
  platform === IntegrationServicePlatforms.DYNAMICS_365_BUSINESS_CENTRAL

export const isMergeServicePlatform = (platform: IntegrationPlatform) =>
  platform === IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE ||
  platform === IntegrationServicePlatforms.MERGE_NETSUITE ||
  platform === IntegrationServicePlatforms.MERGE_SAGE_INTACCT ||
  platform === IntegrationServicePlatforms.MERGE_DYNAMICS_365_BUSINESS_CENTRAL

export const isFullBillTypeSyncPlatform = (platform: IntegrationPlatform) =>
  isMergeServicePlatform(platform) ||
  platform === IntegrationServicePlatforms.WORKATO

export const isIntegrationServicePlatform = (
  platform: IntegrationPlatform
): platform is IntegrationServicePlatform =>
  platform === IntegrationServicePlatforms.NETSUITE ||
  platform === IntegrationServicePlatforms.WORKATO ||
  isCodatServicePlatform(platform) ||
  isMergeServicePlatform(platform)

export const isQboServicePlatform = (platform: IntegrationPlatform) =>
  platform === IntegrationServicePlatforms.QUICKBOOKS_ONLINE ||
  platform === IntegrationServicePlatforms.QUICKBOOKS_ONLINE_SANDBOX ||
  platform === IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE

export const getIntegrationStatus = (
  integration: Components.Schemas.IntegrationResponse
): IntegrationSetupState => {
  let setupState: IntegrationSetupState =
    IntegrationSetupStates.AWAITING_CONFIGURATION
  const platform = integration?.name
  if (isCodatServicePlatform(platform)) {
    const config = integration?.config as IntegrationCodatConfig
    const status = integration?.status

    if (status === IntegrationStatuses.DISABLED) {
      setupState = IntegrationSetupStates.DISABLED
    } else if (
      status === IntegrationStatuses.ACTIVE &&
      config?.status === IntegrationCodatConfigStatuses.LINKED
    ) {
      setupState = IntegrationSetupStates.ACTIVE
    } else {
      setupState = IntegrationSetupStates.AWAITING_CONFIGURATION
    }
  }

  if (platform === IntegrationServicePlatforms.NETSUITE) {
    const status = integration?.status

    if (status === IntegrationStatuses.ACTIVE) {
      setupState = IntegrationSetupStates.ACTIVE
    } else if (status === IntegrationStatuses.DISABLED) {
      setupState = IntegrationSetupStates.DISABLED
    } else {
      // If there is no previous configuration or the integration has been previously deleted,
      // then the integration will need to be reconfigured
      setupState = IntegrationSetupStates.AWAITING_CONFIGURATION
    }
  }

  if (
    isMergeServicePlatform(platform) ||
    platform === IntegrationServicePlatforms.WORKATO
  ) {
    const status = integration?.status

    switch (status) {
      case IntegrationStatuses.ACTIVE:
        return IntegrationSetupStates.ACTIVE
      case IntegrationStatuses.DISABLED:
        return IntegrationSetupStates.DISABLED
      case IntegrationStatuses.DISCONNECTED:
        return IntegrationSetupStates.DISCONNECTED
      case IntegrationStatuses.INITIALIZING:
        return IntegrationSetupStates.INITIALIZING
      default:
        return IntegrationSetupStates.AWAITING_CONFIGURATION
    }
  }

  return setupState
}

export const isActiveIntegration = (
  integration: Components.Schemas.IntegrationResponse
): boolean => {
  if (!isIntegrationServicePlatform(integration?.name)) return false
  return getIntegrationStatus(integration) === IntegrationSetupStates.ACTIVE
}

export const isPushPlatformName = (name?: string | null): boolean => {
  switch (name) {
    case IntegrationServicePlatforms.QUICKBOOKS_ONLINE:
    case IntegrationServicePlatforms.QUICKBOOKS_ONLINE_SANDBOX:
    case IntegrationServicePlatforms.SAGE_INTACCT:
    case IntegrationServicePlatforms.DYNAMICS_365_BUSINESS_CENTRAL:
    case IntegrationServicePlatforms.NETSUITE:
    case IntegrationServicePlatforms.MERGE_QUICKBOOKS_ONLINE:
    case IntegrationServicePlatforms.MERGE_NETSUITE:
    case IntegrationServicePlatforms.MERGE_SAGE_INTACCT:
    case IntegrationServicePlatforms.MERGE_DYNAMICS_365_BUSINESS_CENTRAL:
      return true
    default:
      return false
  }
}

export const isPushIntegration = (
  integration: Components.Schemas.IntegrationResponse
): boolean => isPushPlatformName(integration?.name)

export const isReadOnlyIntegration = (
  integration: Components.Schemas.IntegrationResponse
): boolean => {
  return integration?.name === IntegrationServicePlatforms.WORKATO
}
